import { Link, useLocation, useRouter } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  Building2,
  CalendarSearch,
  History,
  LogOut,
  PanelLeft,
  PanelLeftClose,
  ScrollText,
  Search,
  Settings,
  UserSearch,
  Webhook,
} from "lucide-react";
import { useState } from "react";
import { BrandLogo } from "@/components/shared/brand-logo";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { signOut, useSession } from "@/lib/auth-client";
import { queryClient } from "@/lib/query";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";

interface NavItem {
  label: string;
  icon: LucideIcon;
  href: string;
  disabled?: boolean;
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();
  const location = useLocation();
  const { isMobile, mobileOpen, setMobileOpen } = useSidebar();
  const { labelPlural } = useBuInstanceLabel();

  const isAdmin = session?.user?.role === "admin";

  const navItems: NavItem[] = [
    { label: "Contract Search", icon: Search, href: "/dashboard/contract-search" },
    { label: "Client Search", icon: UserSearch, href: "/dashboard/client-search" },
    {
      label: "PrivateHire Search",
      icon: CalendarSearch,
      href: "/dashboard/private-hire-search",
    },
    { label: "Copy Log", icon: History, href: "/dashboard/copy-operations" },
    // Read-only for non-admins — MasterDataMappingsTable gates the mutation
    // affordances (New/Edit/Delete/Sync) itself via useSession().
    {
      label: "Master Data Mappings",
      icon: ArrowLeftRight,
      href: "/dashboard/master-data-mappings",
    },
  ];

  const adminNavItems: NavItem[] = [
    { label: labelPlural, icon: Building2, href: "/dashboard/bu-instances" },
    { label: "API Call Log", icon: Webhook, href: "/dashboard/soap-call-log" },
    { label: "Settings", icon: Settings, href: "/dashboard/settings" },
    { label: "User Action - Audit Log", icon: ScrollText, href: "/dashboard/audit-log" },
  ];

  async function handleSignOut() {
    await signOut();
    queryClient.clear();
    router.navigate({ to: "/login" });
  }

  function handleNavClick() {
    if (isMobile) {
      setMobileOpen(false);
    }
  }

  // On mobile, don't show collapsed state — always show full sidebar
  const showLabels = isMobile ? true : !collapsed;

  function renderNavItem(item: NavItem) {
    const isActive = location.pathname.startsWith(item.href);

    return (
      <Link
        key={item.label}
        to={item.href}
        onClick={handleNavClick}
        className={cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-sidebar-accent text-sidebar-primary shadow-[inset_2px_0_0_var(--nymbl-g-cyan)]"
            : "text-sidebar-foreground/60",
          item.disabled && "pointer-events-none opacity-40",
          !item.disabled && !isActive && "hover:bg-sidebar-accent hover:text-sidebar-foreground",
        )}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {showLabels && <span>{item.label}</span>}
        {showLabels && item.disabled && (
          <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
            Soon
          </span>
        )}
      </Link>
    );
  }

  const sidebarContent = (
    <aside
      className={cn(
        "flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200",
        isMobile ? "w-64 h-full" : collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Brand */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        <div className="flex items-center gap-2 min-w-0">
          {showLabels ? (
            <BrandLogo />
          ) : (
            <img src="/nymbl-fox-white-btn.png" alt="" className="h-6 w-6 shrink-0" />
          )}
          {showLabels && (
            <span className="font-display text-lg font-bold text-sidebar-foreground truncate">
              Fleet Relay
            </span>
          )}
        </div>
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-sidebar-foreground shrink-0"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2">
        {navItems.map(renderNavItem)}

        {isAdmin && (
          <>
            <div className="my-2 border-t border-sidebar-border" />
            {showLabels && (
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
                Admin
              </p>
            )}
            {adminNavItems.map(renderNavItem)}
          </>
        )}
      </nav>

      {/* User info + sign out */}
      <div className="border-t border-sidebar-border p-2">
        {session?.user && (
          <Link
            to="/dashboard/profile"
            onClick={handleNavClick}
            className={cn(
              "mb-2 flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-sidebar-accent",
              !showLabels && "justify-center",
            )}
          >
            <UserAvatar user={session.user} className="h-8 w-8" />
            {showLabels && (
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-sidebar-foreground">
                  {session.user.name}
                </p>
                <p className="truncate text-xs text-muted-foreground">{session.user.email}</p>
              </div>
            )}
          </Link>
        )}
        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {showLabels && <span>Sign Out</span>}
        </button>
      </div>
    </aside>
  );

  // Mobile: overlay sidebar
  if (isMobile) {
    if (!mobileOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
        {/* Slide-out sidebar */}
        <div className="fixed left-0 top-0 z-50 h-full">{sidebarContent}</div>
      </>
    );
  }

  // Desktop: normal sidebar
  return sidebarContent;
}
